import { HeaderModel } from '@/types';
import Stack from '../animation/Stack';
import ResponsiveImage from '../general/ResponsiveImage';
import Box from '../animation/Box';
import { fadeInDown } from '@/animations';
import LoginLogoutBtn from './LoginLogoutBtn';
import Link from '../general/Link';
import DesktopNav from './DesktopNav';
import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import MobileNav from './MobileNav';

interface IHeaderModel {
  data?: HeaderModel;
  transparentHeader: boolean;
  animate?: boolean;
  fullPageSlider?: boolean;
}

const Header = ({ data, transparentHeader, fullPageSlider, ...props }: IHeaderModel) => {
  const [solidBG, setSolidBG] = useState(!transparentHeader);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const prevScrollPos = useRef<number>(0);

  useEffect(() => {
    const onScroll = () => {
      const currentScrollPos = window.scrollY;

      if (transparentHeader) {
        if (fullPageSlider) {
          setSolidBG(currentScrollPos > 100);
        } else {
          const style = window.getComputedStyle(document.body);
          const firstModule = document.querySelector('section.module');
          const headerHeight = parseInt(style.getPropertyValue('--header-height'), 10);
          if (
            firstModule?.classList.contains('ClassicLetterbox') &&
            firstModule.querySelector('.FullPageEmbeddedMediaElement')
          ) {
            setSolidBG(
              currentScrollPos >=
                (firstModule.querySelector('.FullPageEmbeddedMediaElement')?.getBoundingClientRect().height ||
                  window.innerHeight) -
                  headerHeight,
            );
          } else {
            setSolidBG(
              currentScrollPos >= (firstModule?.getBoundingClientRect().height || window.innerHeight) - headerHeight,
            );
          }
        }
      }

      prevScrollPos.current = currentScrollPos;
    };

    onScroll();

    window.removeEventListener('scroll', onScroll);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [fullPageSlider, transparentHeader]);

  if (!data) return null;

  return (
    <Stack
      tag="header"
      className={classNames(
        'main-header group fixed top-0 right-0 left-0 z-50 h-[var(--header-height)] border-b border-solid transition-colors duration-500 ease-out',
        'print:relative',
        solidBG || mobileNavOpen
          ? 'solid-bg border-[#E9E9E9] bg-white'
          : 'transparent-bg border-transparent bg-white md:bg-transparent',
      )}
      {...props}
    >
      <Box animation={fadeInDown} className="no-motion-on-mobile container flex h-full items-center justify-between">
        <Link link={{ href: '/' }} className="flex h-full items-center justify-center outline-none">
          {data.logos && data.logos.length === 1 && data.logos[0]?.logoImage && (
            <div className="w-[140px] xl:w-[180px]">
              <ResponsiveImage
                widths={{ xs: null }}
                image={data.logos[0]?.logoImage}
                dataNotLazy
                className={classNames(
                  'filter-black md:group-[.transparent-bg]:filter-white block transition-all duration-500',
                )}
              />
            </div>
          )}
        </Link>
        <DesktopNav links={data.links} className="hidden lg:flex print:hidden" />
        <MobileNav
          data={data}
          className="lg:hidden print:hidden"
          open={mobileNavOpen}
          toogleOpen={() => setMobileNavOpen(!mobileNavOpen)}
        />
        <div className="hidden items-center gap-2.5 lg:flex print:hidden">
          {data.highlightedButton && (
            <Link
              link={data.highlightedButton}
              className="btn primary v2 !bg-yellow !border-yellow !px-5 !py-[11px] !text-sm !font-normal !text-black"
            />
          )}
          {(data.loginLink || data.logoutLink) && (
            <LoginLogoutBtn loginLink={data.loginLink} logoutLink={data.logoutLink} className="!text-sm !font-normal" />
          )}
        </div>
      </Box>
    </Stack>
  );
};

export default Header;
